import {
  Mail,
  FileText,
  Calendar,
  Building2,
  Kanban,
  Smartphone,
  Users,
  MessageSquareText,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import { isInstitutionSettingsTab } from '@/lib/institution-tab-registry';
import { isLyrraOne } from '@/lib/platform';

export const LYRRAONE_EXTRA_SETTINGS_TAB_IDS = [
  'seller-billing',
  'email-smtp',
  'email-templates',
  'sms',
  'sms-templates',
  'google-calendar',
  'crm-pipeline',
  'team',
  'site',
] as const;
export type LyrraOneExtraSettingsTabId = (typeof LYRRAONE_EXTRA_SETTINGS_TAB_IDS)[number];

export type LyrraOneSettingsCategoryId =
  | 'account'
  | 'communication'
  | 'sales'
  | 'content'
  | 'integrations';

export interface LyrraOneSettingsCategoryDef {
  id: LyrraOneSettingsCategoryId;
  titleKey: string;
  titleFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
}

/** Ordre d’affichage des catégories sur `/settings`. */
export const LYRRAONE_SETTINGS_CATEGORIES: LyrraOneSettingsCategoryDef[] = [
  {
    id: 'account',
    titleKey: 'lyrraone.settings.category.account',
    titleFallback: 'Compte & équipe',
    descriptionKey: 'lyrraone.settings.category.accountDesc',
    descriptionFallback: 'Société, facturation vendeur et accès collaborateurs',
  },
  {
    id: 'communication',
    titleKey: 'lyrraone.settings.category.communication',
    titleFallback: 'Communication',
    descriptionKey: 'lyrraone.settings.category.communicationDesc',
    descriptionFallback: 'Emails, templates et SMS',
  },
  {
    id: 'sales',
    titleKey: 'lyrraone.settings.category.sales',
    titleFallback: 'Vente & rendez-vous',
    descriptionKey: 'lyrraone.settings.category.salesDesc',
    descriptionFallback: 'CRM, calendrier et prise de rendez-vous',
  },
  {
    id: 'content',
    titleKey: 'lyrraone.settings.category.content',
    titleFallback: 'Contenu & organisation',
    descriptionKey: 'lyrraone.settings.category.contentDesc',
    descriptionFallback: 'Site web, catégories, groupes, webinaires et corbeille',
  },
  {
    id: 'integrations',
    titleKey: 'lyrraone.settings.category.integrations',
    titleFallback: 'Intégrations & données',
    descriptionKey: 'lyrraone.settings.category.integrationsDesc',
    descriptionFallback: 'Webhooks, MCP et tableaux BI',
  },
];

export interface LyrraOneExtraSettingsTabDef {
  id: LyrraOneExtraSettingsTabId;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  category: LyrraOneSettingsCategoryId;
}

export const lyrraOneExtraSettingsTabDefs: LyrraOneExtraSettingsTabDef[] = [
  {
    id: 'seller-billing',
    labelKey: 'lyrraone.settings.sellerBilling',
    labelFallback: 'Facturation & société',
    icon: Building2,
    category: 'account',
  },
  {
    id: 'team',
    labelKey: 'lyrraone.settings.team',
    labelFallback: 'Équipe',
    icon: Users,
    category: 'account',
  },
  {
    id: 'email-smtp',
    labelKey: 'lyrraone.settings.emailSmtp',
    labelFallback: 'Serveur SMTP',
    icon: Mail,
    category: 'communication',
  },
  {
    id: 'email-templates',
    labelKey: 'lyrraone.settings.emailTemplates',
    labelFallback: 'Templates email',
    icon: FileText,
    category: 'communication',
  },
  {
    id: 'sms',
    labelKey: 'lyrraone.settings.sms',
    labelFallback: 'Envoi SMS',
    icon: Smartphone,
    category: 'communication',
  },
  {
    id: 'sms-templates',
    labelKey: 'lyrraone.settings.smsTemplates',
    labelFallback: 'Templates SMS',
    icon: MessageSquareText,
    category: 'communication',
  },
  {
    id: 'crm-pipeline',
    labelKey: 'lyrraone.settings.crmPipeline',
    labelFallback: 'Pipeline CRM',
    icon: Kanban,
    category: 'sales',
  },
  {
    id: 'google-calendar',
    labelKey: 'lyrraone.settings.googleCalendar',
    labelFallback: 'Calendrier',
    icon: Calendar,
    category: 'sales',
  },
  {
    id: 'site',
    labelKey: 'lyrraone.settings.site',
    labelFallback: 'Paramètres du site',
    icon: Globe,
    category: 'content',
  },
];

/** Mapping des onglets institution (surface settings) → catégories hub. */
export const LYRRAONE_INSTITUTION_SETTINGS_TAB_CATEGORY: Partial<
  Record<string, LyrraOneSettingsCategoryId>
> = {
  categories: 'content',
  webinars: 'content',
  groups: 'content',
  trash: 'content',
  'bi-studio': 'integrations',
  webhooks: 'integrations',
  mcp: 'integrations',
};

export function lyrraOneSettingsCategoryForTab(tabId: string): LyrraOneSettingsCategoryId {
  const extra = lyrraOneExtraSettingsTabDefs.find((t) => t.id === tabId);
  if (extra) return extra.category;
  return LYRRAONE_INSTITUTION_SETTINGS_TAB_CATEGORY[tabId] ?? 'content';
}

export function isLyrraOneExtraSettingsTab(tab: string | null): tab is LyrraOneExtraSettingsTabId {
  if (!tab) return false;
  return LYRRAONE_EXTRA_SETTINGS_TAB_IDS.includes(tab as LyrraOneExtraSettingsTabId);
}

export function lyrraOneExtraSettingsTabPath(tab: LyrraOneExtraSettingsTabId): string {
  return `/settings?tab=${tab}`;
}

/** Onglets valides sous `/settings` sur LyrraOne (institution + marketing). */
export function isLyrraOneSettingsTab(tab: string | null): boolean {
  if (!isLyrraOne()) return isInstitutionSettingsTab(tab);
  return isInstitutionSettingsTab(tab) || isLyrraOneExtraSettingsTab(tab);
}
